/**
 * Last-known panel → child elementId membership for Swing-parity panel setVisible.
 *
 * Panel.vue uses v-if on panelVisible: hide unmounts children, so registry propagate
 * cannot revive them. Keep membership across unRegisterElement and sync shell/deferred
 * when setVisible("panel", id, visible) runs with propagateChildren.
 */

export type PanelChildShellSyncCallbacks = {
  setElementShellVisibleById: (elementId: string, visible: boolean) => void
  rememberDeferredElementShellById: (elementId: string, visible: boolean) => void
}

/** Mutable index: panelId → Set<xml element id>. */
export type PanelChildMembershipIndex = Map<string, Set<string>>

export function createPanelChildMembershipIndex(): PanelChildMembershipIndex {
  return new Map()
}

export function rememberPanelChildMembership(
  index: PanelChildMembershipIndex,
  panelId: string | number | null | undefined,
  elementId: string | number | null | undefined,
): void {
  const panelKey = String(panelId ?? '').trim()
  const elementKey = String(elementId ?? '').trim()
  if (!panelKey || !elementKey) return
  let set = index.get(panelKey)
  if (!set) {
    set = new Set()
    index.set(panelKey, set)
  }
  set.add(elementKey)
}

export function listPanelChildElementIds(
  index: PanelChildMembershipIndex,
  panelId: string | number | null | undefined,
): string[] {
  const panelKey = String(panelId ?? '').trim()
  if (!panelKey) return []
  const set = index.get(panelKey)
  return set ? [...set] : []
}

/**
 * Sync shell + deferred visibility for all known children of a panel
 * (including unmounted ones). Safe to call after registry propagate.
 */
export function syncPanelChildrenShellVisibility(
  index: PanelChildMembershipIndex,
  panelId: string | number | null | undefined,
  visible: boolean,
  callbacks: PanelChildShellSyncCallbacks,
): string[] {
  const ids = listPanelChildElementIds(index, panelId)
  for (const elementId of ids) {
    callbacks.setElementShellVisibleById(elementId, visible)
    callbacks.rememberDeferredElementShellById(elementId, visible)
  }
  return ids
}

/** Compare panel registry ids without number/string mismatch (applyPanelVisibility). */
export function panelIdsEqual(
  left: string | number | null | undefined,
  right: string | number | null | undefined,
): boolean {
  const a = String(left ?? '').trim()
  const b = String(right ?? '').trim()
  return a !== '' && a === b
}
